import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FloppyDisk, PaperPlaneTilt, CheckCircle, Plus, Trash, ArrowUUpLeft, Blueprint, ClipboardText, FilePdf, FileText, ChartBar } from "@phosphor-icons/react";
import { api, formatApiErrorDetail, shortDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const CONTACT_FIELDS = [
  ["organization", "Organization", "text"],
  ["program_name", "Program name", "text"],
  ["your_contact", "Your contact (name, title, email)", "text"],
  ["dmh_program_lead", "Program lead", "text"],
  ["dmh_grant_manager", "Grant manager", "text"],
  ["return_by", "Return by", "date"],
  ["date_completed", "Date completed", "date"],
];

const PARTS = [
  { title: "Part 1 · The problem", note: "These answers become the background of your Scope of Work and the start of your program plan.", qs: [
    ["q1", "In one sentence, what are you trying to change in your community?"],
    ["q2", "What numbers show this problem exists in your area? (Include the source and the year each number covers.)"],
    ["q3", "Where or when is this problem less severe, and what's different there?"],
    ["q4", "Who did you hear from in shaping this program?"],
    ["q5", "Which counties or sites will you serve?"],
    ["q6", "Who else already works on this problem locally, and what gap does your program fill?"],
    ["q7", "How does this program fit your organization's mission?"],
  ]},
  { title: "Part 2 · The people", note: "These set who your contract serves and the numbers you'll plan around.", qs: [
    ["q8", "Who exactly will you serve? (Age, population, eligibility, or any other way you'd describe them.)"],
    ["q9", "How many people do you expect to serve this year, and why is that number realistic?"],
  ]},
  { title: "Part 3 · The plan", note: "These become your goals, activities, and the services written into your Scope of Work.", qs: [
    ["q10", "If this program works, what will be different for the people you serve 12 months from now?"],
    ["q11", "What will be different in 3 to 5 years? (Think big — this shapes your vision, not your contract targets.)"],
    ["q12", "What are the 3 to 5 core things your program will do? (Number them.)"],
    ["q13", "For each core activity: how often, how many, by when, and where?"],
    ["q14", "What will happen in your first 90 days?"],
    ["q15", "Who will do the work? (Staff positions, % time, whether hired, plus any partners/subcontractors.)"],
    ["q16", "What will this funding not pay for?"],
  ]},
  { title: "Part 5 · Staying power", note: "These become the risks and sustainability section and connect your budget to your activities.", qs: [
    ["q22", "What has to go right, what could get in the way, and what will you do if a quarter falls short?"],
    ["q23", "How did you arrive at your costs, and which budget lines pay for which activities?"],
    ["q24", "What happens to this program when this funding ends?"],
  ]},
];

const ACTIVITY_ROWS = [
  ["count", "17 · What you'll count", "Start with \"Number of…\""],
  ["change", "18 · What should change", "Start with \"Increase in…\" or \"Decrease in…\""],
  ["source", "19 · Source, calculation, who pulls it", ""],
  ["targets", "20 · Now, end of Q2, year-end", "If new, write \"new\" for now."],
  ["usefulness", "21 · How you'll know it was useful", ""],
];

const STATUS_BADGE = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  submitted: "bg-amber-50 text-amber-700 border-amber-300",
  reviewed: "bg-emerald-50 text-emerald-700 border-emerald-300",
};

export default function ProgramPlan({ contractId, isStaff = false }) {
  const [plans, setPlans] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [contact, setContact] = useState({});
  const [answers, setAnswers] = useState({});
  const [activities, setActivities] = useState([]);
  const [view, setView] = useState("worksheet");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get(`/planning/contracts/${contractId}/plans`);
      setPlans(data);
      if (data.length && !activeId) setActiveId(data[0].id);
      if (!data.length) { setActiveId(null); setPlan(null); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [contractId, activeId]);

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [contractId]);

  const loadPlan = useCallback(async (id) => {
    const { data } = await api.get(`/planning/plans/${id}`);
    setPlan(data); setContact(data.contact || {}); setAnswers(data.answers || {});
    setActivities(data.activities || []); setNotes(data.review?.notes || "");
  }, []);

  useEffect(() => { if (activeId) loadPlan(activeId); }, [activeId, loadPlan]);

  const startYear = async (copy) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/planning/contracts/${contractId}/plans`, copy ? {} : { contract_year: 1 });
      await loadList(); setActiveId(data.id); setView("worksheet");
      toast.success(`Year ${data.contract_year} worksheet started`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const save = async (silent) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/planning/plans/${plan.id}`, { contact, answers, activities });
      setPlan(data); if (!silent) toast.success("Saved");
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!(await save(true))) return;
    try { const { data } = await api.post(`/planning/plans/${plan.id}/submit`); setPlan(data); loadList(); toast.success("Submitted for review"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const review = async (reopen) => {
    try {
      const { data } = await api.post(`/planning/plans/${plan.id}/review`, { notes, reopen });
      setPlan(data); loadList();
      toast.success(reopen ? "Reopened for edits" : "Marked as reviewed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canEdit = plan && (plan.status !== "reviewed" || isStaff);
  const setA = (k) => (e) => setAnswers({ ...answers, [k]: e.target.value });

  const downloadPdf = () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    window.open(`${API}/api/planning/plans/${plan.id}/export/pdf`, "_blank");
  };

  const draftSow = async () => {
    if (!window.confirm("Draft the Scope of Work from this plan's answers? This replaces the current SOW builder draft on the contract.")) return;
    setBusy(true);
    try {
      await api.put(`/contracts/${contractId}/sow/from-plan`, { plan_id: plan.id });
      toast.success("Scope of Work draft created — open the SOW tab to review and refine it");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!plan && plans.length === 0) {
    return (
      <div className="bg-card border border-dashed border-border rounded-md p-8 text-center" data-testid="program-plan-empty">
        <ClipboardText size={30} className="mx-auto text-muted-foreground" />
        <div className="font-head font-semibold mt-3">Program Planning Worksheet</div>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
          Think through your program for this contract — the problem, people, plan, proof, and staying power.
          Your answers build a program plan and a logic model, reassessed each contract year.
        </p>
        <Button className="mt-4" data-testid="program-plan-start" disabled={busy} onClick={() => startYear(false)}>
          <Plus size={16} className="mr-1.5" /> Start Year 1 worksheet
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="program-plan">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {plans.map((p) => (
          <button key={p.id} data-testid={`program-plan-year-${p.contract_year}`} onClick={() => setActiveId(p.id)}
            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${activeId === p.id ? "bg-primary text-white border-primary" : "bg-card border-border hover:border-primary"}`}>
            Year {p.contract_year}
          </button>
        ))}
        <Button size="sm" variant="outline" data-testid="program-plan-new-year" disabled={busy} onClick={() => startYear(true)}>
          <Plus size={15} className="mr-1" /> Reassess (new year)
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {plan && <span className={`text-[11px] px-2 py-0.5 rounded-sm border capitalize ${STATUS_BADGE[plan.status]}`} data-testid="program-plan-status">{plan.status}</span>}
          {plan && <Button size="sm" variant="outline" data-testid="program-plan-pdf" onClick={downloadPdf}><FilePdf size={15} className="mr-1" /> PDF</Button>}
          {plan && isStaff && <Button size="sm" variant="outline" data-testid="program-plan-draft-sow" disabled={busy} onClick={draftSow}><FileText size={15} className="mr-1" /> Draft SOW</Button>}
          <div className="flex rounded-md border border-border overflow-hidden">
            <button data-testid="program-plan-view-worksheet" onClick={() => setView("worksheet")} className={`px-2.5 py-1 text-xs flex items-center gap-1 ${view === "worksheet" ? "bg-primary text-white" : "bg-card"}`}><ClipboardText size={13} /> Worksheet</button>
            <button data-testid="program-plan-view-scorecard" onClick={() => setView("scorecard")} className={`px-2.5 py-1 text-xs flex items-center gap-1 ${view === "scorecard" ? "bg-primary text-white" : "bg-card"}`}><ChartBar size={13} /> Scorecard</button>
            <button data-testid="program-plan-view-logic" onClick={() => setView("logic")} className={`px-2.5 py-1 text-xs flex items-center gap-1 ${view === "logic" ? "bg-primary text-white" : "bg-card"}`}><Blueprint size={13} /> Logic model</button>
          </div>
        </div>
      </div>

      {plan && view === "logic" && <LogicModel lm={plan.logic_model} />}

      {plan && view === "scorecard" && <Scorecard rows={plan.scorecard} />}

      {plan && view === "worksheet" && (
        <div className="space-y-5">
          {plan.status === "reviewed" && plan.review?.notes && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-sm" data-testid="program-plan-review-note">
              <div className="font-medium text-emerald-800 flex items-center gap-1.5"><CheckCircle size={15} weight="fill" /> Reviewed by {plan.review.reviewed_by} · {shortDate(plan.review.reviewed_at)}</div>
              <div className="text-emerald-900 mt-1">{plan.review.notes}</div>
            </div>
          )}

          <Section title="Contact information">
            <div className="grid sm:grid-cols-2 gap-3">
              {CONTACT_FIELDS.map(([k, label, type]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-xs">{label}</Label>
                  <Input data-testid={`pp-contact-${k}`} type={type} disabled={!canEdit} value={contact[k] || ""} onChange={(e) => setContact({ ...contact, [k]: e.target.value })} />
                </div>
              ))}
            </div>
          </Section>

          {PARTS.map((part) => (
            <Section key={part.title} title={part.title} note={part.note}>
              <div className="space-y-4">
                {part.qs.map(([k, q]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-sm font-normal text-foreground">{q}</Label>
                    <Textarea data-testid={`pp-${k}`} disabled={!canEdit} value={answers[k] || ""} onChange={setA(k)} className="min-h-[60px]" placeholder={canEdit ? "Short answers and bullet points are fine. Not sure yet? Write \"not sure\"." : ""} />
                  </div>
                ))}
              </div>
            </Section>
          ))}

          <Section title="Part 4 · The proof" note="One table per core activity from question 12. These become the rows of your quarterly report.">
            <div className="space-y-4">
              {activities.map((act, i) => (
                <div key={i} className="border border-border rounded-md p-4 bg-secondary/30" data-testid={`pp-activity-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Input data-testid={`pp-activity-title-${i}`} disabled={!canEdit} value={act.title || ""} onChange={(e) => { const n = [...activities]; n[i] = { ...act, title: e.target.value }; setActivities(n); }} className="h-8 w-56 font-medium bg-card" placeholder={`Activity ${i + 1}`} />
                    {canEdit && activities.length > 1 && (
                      <Button size="icon" variant="ghost" data-testid={`pp-activity-remove-${i}`} onClick={() => setActivities(activities.filter((_, j) => j !== i))}><Trash size={15} className="text-alert" /></Button>
                    )}
                  </div>
                  <div className="space-y-2.5">
                    {ACTIVITY_ROWS.map(([k, label, hint]) => (
                      <div key={k} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{label}{hint ? <span className="ml-1 italic">— {hint}</span> : ""}</Label>
                        <Textarea data-testid={`pp-activity-${i}-${k}`} disabled={!canEdit} value={act[k] || ""} onChange={(e) => { const n = [...activities]; n[i] = { ...act, [k]: e.target.value }; setActivities(n); }} className="min-h-[46px] bg-card" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-border/70">
                    <Label className="text-xs text-muted-foreground font-medium">Track your numbers — year-end target and cumulative actuals per quarter</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                      <div className="space-y-1">
                        <Label className="text-[11px] text-muted-foreground">Year-end target</Label>
                        <Input data-testid={`pp-activity-${i}-target`} inputMode="numeric" disabled={!canEdit} value={act.target_value ?? ""} onChange={(e) => { const n = [...activities]; n[i] = { ...act, target_value: e.target.value }; setActivities(n); }} className="h-8 bg-card" placeholder="e.g. 400" />
                      </div>
                      {["1", "2", "3", "4"].map((q) => (
                        <div key={q} className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Q{q} actual</Label>
                          <Input data-testid={`pp-activity-${i}-actual-${q}`} inputMode="numeric" disabled={!canEdit} value={(act.actuals || {})[q] ?? ""} onChange={(e) => { const n = [...activities]; n[i] = { ...act, actuals: { ...(act.actuals || {}), [q]: e.target.value } }; setActivities(n); }} className="h-8 bg-card" placeholder="—" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              {canEdit && (
                <Button variant="outline" size="sm" data-testid="pp-activity-add" onClick={() => setActivities([...activities, { title: `Activity ${activities.length + 1}`, count: "", change: "", source: "", targets: "", usefulness: "", target_value: "", actuals: {} }])}>
                  <Plus size={15} className="mr-1" /> Add activity
                </Button>
              )}
            </div>
          </Section>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 sticky bottom-0 bg-background/90 backdrop-blur py-3 border-t border-border">
            {canEdit && <Button data-testid="program-plan-save" disabled={busy} onClick={() => save(false)}><FloppyDisk size={16} className="mr-1.5" /> {busy ? "Saving…" : "Save changes"}</Button>}
            {canEdit && plan.status === "draft" && <Button variant="outline" data-testid="program-plan-submit" disabled={busy} onClick={submit}><PaperPlaneTilt size={16} className="mr-1.5" /> Submit for review</Button>}
            {isStaff && plan.status === "submitted" && (
              <div className="flex items-end gap-2 flex-1 min-w-[260px]">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-muted-foreground">Reviewer note (optional)</Label>
                  <Input data-testid="program-plan-review-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Feedback for the grantee…" />
                </div>
                <Button data-testid="program-plan-mark-reviewed" onClick={() => review(false)}><CheckCircle size={16} className="mr-1.5" /> Mark reviewed</Button>
              </div>
            )}
            {isStaff && plan.status === "reviewed" && (
              <Button variant="outline" data-testid="program-plan-reopen" onClick={() => review(true)}><ArrowUUpLeft size={16} className="mr-1.5" /> Reopen for edits</Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <div className="bg-card border border-border rounded-md p-5">
      <div className="font-head font-semibold">{title}</div>
      {note && <p className="text-xs text-muted-foreground mt-0.5 mb-3">{note}</p>}
      <div className={note ? "" : "mt-3"}>{children}</div>
    </div>
  );
}

const LM_COLS = [
  ["inputs", "Inputs", "Staff, funding & partners"],
  ["activities", "Activities", "What your program does"],
  ["outputs", "Outputs", "What you produce & count"],
  ["outcomes", "Outcomes", "What changes for people"],
  ["impact", "Impact", "The long-term vision"],
];

function LogicModel({ lm }) {
  if (!lm) return null;
  return (
    <div data-testid="program-plan-logic-model">
      <div className="grid gap-3 lg:grid-cols-5">
        {LM_COLS.map(([k, label, sub], idx) => (
          <div key={k} className="bg-card border border-border rounded-md overflow-hidden flex flex-col" data-testid={`lm-${k}`}>
            <div className="bg-primary text-white px-3 py-2">
              <div className="font-head font-semibold text-sm flex items-center gap-1.5">{idx + 1}. {label}</div>
              <div className="text-[10px] text-white/70">{sub}</div>
            </div>
            <div className="p-3 space-y-2 flex-1">
              {(lm[k] || []).length === 0 ? (
                <div className="text-xs text-muted-foreground italic">Fill in the worksheet to populate this.</div>
              ) : lm[k].map((v, i) => (
                <div key={i} className="text-xs text-foreground bg-secondary/50 rounded p-2 whitespace-pre-wrap">{v}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3 mt-3">
        {[["problem", "The problem"], ["population", "Who we serve"], ["assumptions", "Assumptions & risks"]].map(([k, label]) => (
          <div key={k} className="bg-card border border-border rounded-md p-3" data-testid={`lm-${k}`}>
            <div className="overline text-muted-foreground mb-1.5">{label}</div>
            {(lm[k] || []).length === 0 ? <div className="text-xs text-muted-foreground italic">—</div>
              : lm[k].map((v, i) => <div key={i} className="text-xs text-foreground mb-1 whitespace-pre-wrap">{v}</div>)}
          </div>
        ))}
      </div>
    </div>
  );
}

const FLAG_META = {
  on_track: { label: "On track", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  at_risk: { label: "At risk", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  behind: { label: "Behind", cls: "bg-red-50 text-red-700 border-red-300" },
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};
const fmtNum = (v) => (v === null || v === undefined ? "—" : Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 1 }));

function Scorecard({ rows }) {
  const list = rows || [];
  const tracked = list.filter((r) => r.target_value !== null && r.target_value !== undefined);
  if (tracked.length === 0) {
    return (
      <div className="bg-card border border-dashed border-border rounded-md p-8 text-center" data-testid="program-plan-scorecard-empty">
        <ChartBar size={28} className="mx-auto text-muted-foreground" />
        <div className="font-head font-semibold mt-3">No targets yet</div>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
          Add a year-end target and quarterly actuals to each Part 4 activity in the Worksheet to see your Targets vs Actuals progress here.
        </p>
      </div>
    );
  }
  const counts = tracked.reduce((a, r) => { a[r.flag] = (a[r.flag] || 0) + 1; return a; }, {});
  return (
    <div data-testid="program-plan-scorecard" className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {["on_track", "at_risk", "behind", "not_started"].filter((f) => counts[f]).map((f) => (
          <span key={f} data-testid={`scorecard-summary-${f}`} className={`text-xs px-2.5 py-1 rounded-sm border ${FLAG_META[f].cls}`}>
            {counts[f]} {FLAG_META[f].label}
          </span>
        ))}
      </div>
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-primary text-white text-left">
              <th className="px-3 py-2 font-head font-semibold">Activity</th>
              <th className="px-3 py-2 font-head font-semibold text-right">Target</th>
              <th className="px-3 py-2 font-head font-semibold text-right">To date</th>
              <th className="px-3 py-2 font-head font-semibold text-right">Expected</th>
              <th className="px-3 py-2 font-head font-semibold text-right">Pace</th>
              <th className="px-3 py-2 font-head font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {tracked.map((r) => (
              <tr key={r.index} data-testid={`scorecard-row-${r.index}`} className="border-t border-border align-top">
                <td className="px-3 py-2.5">
                  <div className="font-medium text-foreground">{r.title}</div>
                  {r.metric && <div className="text-xs text-muted-foreground mt-0.5">{r.metric}</div>}
                  {r.latest_quarter && <div className="text-[11px] text-muted-foreground mt-0.5">as of Q{r.latest_quarter}</div>}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtNum(r.target_value)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmtNum(r.actual_to_date)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">{fmtNum(r.expected_to_date)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{r.pace === null || r.pace === undefined ? "—" : `${Math.round(r.pace * 100)}%`}</td>
                <td className="px-3 py-2.5">
                  <span data-testid={`scorecard-flag-${r.index}`} className={`text-[11px] px-2 py-0.5 rounded-sm border whitespace-nowrap ${FLAG_META[r.flag].cls}`}>{FLAG_META[r.flag].label}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Pace compares cumulative actuals against the expected amount by the latest reported quarter (target × quarter ÷ 4).
        On track ≥ 90%, At risk 70–89%, Behind &lt; 70%.
      </p>
    </div>
  );
}
